import {
    APP_AUTH,
    changed_status,
    DIRECT_ROUTING,
    initServiceData,
    loaded_status,
    menuAppAuthMapping,
    OPERATOR_CONNECT,
    statusDisplayExtendMapping,
    ZOOM,
    EIGHTBYEIGHT,
    EIGHTBYEIGHT_AUTH,
    EIGHTBYEIGHT_STATUS_DISPLAY_DETAIL_TO_STATUS,
    eightByEightData,
    GCC_HIGH,
    WEBEX,
} from './constants';
import {
    isSimpleRedirectAuthServiceType,
    isGenericSimpleServiceProfileServiceType,
    getPendingAccessPlatform,
    PENDING_ACCESS_AUTH_SERVICE_TYPES,
} from 'services/platformRegistry';

/**
 * Resolve an 8x8 status entry ({ statusLabel, color, ... }) from the list-level
 * statusDisplay.statusDisplayDetail. Used when the numeric per-service status is
 * absent/unknown (e.g. old services whose GET /Service/{id} returns 500).
 */
function getEightByEightStatusFromDisplay(statusDisplayDetail) {
    const mapped =
        EIGHTBYEIGHT_STATUS_DISPLAY_DETAIL_TO_STATUS[statusDisplayDetail];
    if (mapped === undefined) return null;
    const entry = eightByEightData.statusId[mapped];
    const label = entry?.statusLabels?.[0];
    return label ? { statusLabel: label, ...entry[label] } : null;
}

/**
 * @param {Object} state
 * @param {CardStatusType} state.cardStatus
 * @param {{type: string; payload: {[k: string]: any; statusDisplay: {statusDisplayLevel: number}}}} action
 * */

function getStatusLabel(
    statusOptions,
    status,
    statusDisplay,
    serviceType,
    isServiceAccountError,
    appAuthService,
) {
    const statusDisplayLevel = statusDisplay?.statusDisplayLevel;
    const statusDisplayDetail = statusDisplay?.statusDisplayDetail;

    const lastModifiedTS = appAuthService?.lastModifiedTS;
    // Has is been less than 5 mins since creating the auth?
    let isAuthlessThanFive = false;
    if (new Date() - new Date(lastModifiedTS + 'Z') < 5 * 60 * 1000) {
        isAuthlessThanFive = true;
    }

    // Early returns for auth-only services where `status` may be absent from the
    // API response and statusDisplay is the only source of truth.
    if (
        getPendingAccessPlatform(serviceType) &&
        statusDisplayDetail === 'PendingAccess'
    ) {
        return 'Pending Access';
    }

    // Old 8x8 services lack a numeric status; derive the label from statusDisplay.
    if (serviceType === EIGHTBYEIGHT && !statusOptions) {
        const entry = getEightByEightStatusFromDisplay(statusDisplayDetail);
        if (entry) return entry.statusLabel;
    }

    if (!statusOptions) {
        return null;
    }

    if (serviceType === APP_AUTH && status === 5) {
        return 'Reinitializing';
    }

    if (isSimpleRedirectAuthServiceType(serviceType)) {
        if (statusDisplayDetail === 'PairPending') {
            return 'Pair Pending';
        }
        if (status === 20 || status === 21) {
            if (serviceType === EIGHTBYEIGHT_AUTH) {
                return 'Incomplete Config';
            }
            if (PENDING_ACCESS_AUTH_SERVICE_TYPES.includes(serviceType)) {
                return 'Pending Access';
            }
            return statusDisplayLevel === 2 ? 'Error' : 'Degraded';
        }
        if (PENDING_ACCESS_AUTH_SERVICE_TYPES.includes(serviceType)) {
            if (statusDisplayDetail === 'PendingAccess' || status === 1) {
                return 'Pending Access';
            }
        }
    }

    if (serviceType === EIGHTBYEIGHT) {
        // Backend EightByEight status enum: 0=Ok, 1=Creating, 2=PendingAccess,
        // 10=UpdatingAddresses, 11=UpdatingSites, 12=CheckingLicensing,
        // 20=Error.
        switch (status) {
            case 0:
                return 'Healthy';
            case 1:
                return 'Creating';
            case 2:
                return 'Pending Access';
            case 10:
                return 'Updating Addresses';
            case 11:
                return 'Updating Sites';
            case 12:
                return 'Checking Licensing';
            case 20:
                return 'Error';
            default:
                // Fallback to first label for unknown statuses
                return statusOptions?.statusLabels?.[0] ?? null;
        }
    }

    if (isGenericSimpleServiceProfileServiceType(serviceType)) {
        if (status === 1 || statusDisplayDetail === 'PendingAccess')
            return 'Pending Access';
        if (status === 2) return 'Access Required';
        return 'Healthy';
    }

    if (serviceType === ZOOM || serviceType === WEBEX) {
        if (status === 2) {
            return 'Access Required';
        } else {
            return 'Healthy';
        }
    }

    if (serviceType === APP_AUTH) {
        if (isServiceAccountError) {
            return 'Service Account Error';
        }
        if (status === 20 || status === 21) {
            return statusDisplayLevel === 2 ? 'Error' : 'Degraded';
        }
    }

    if (serviceType === DIRECT_ROUTING) {
        if (status === 2) {
            if (appAuthService?.status === 4) {
                return 'Confirming Access';
            }
            if (appAuthService?.graphAuth) {
                if (isAuthlessThanFive) {
                    return 'Confirming Access';
                }
                if (!appAuthService?.company?.approved) {
                    return 'Approval Required';
                } else {
                    return 'Access Required';
                }
            } else {
                return 'Access Required';
            }
        }

        if (status === 4) {
            return 'Initializing';
        }

        if (status === 10) {
            if (statusDisplayDetail?.includes('Missing Regions')) {
                return 'Region not set';
            }
            return 'Provisioning DNS';
        }

        if (status === 11 || status === 12 || status === 13) {
            if (statusDisplayDetail?.includes('Missing Voice Policies')) {
                return 'No Voice Policies';
            } else if (statusDisplayDetail?.includes('Missing Dial Plans')) {
                return 'No Dial Plans';
            } else if (status === 11) {
                return 'Provisioning SBC';
            } else if (status === 12) {
                return 'Activating Teams Phone';
            }
            return 'Provisioning Teams Phone';
        }
    }

    if (serviceType === GCC_HIGH) {
        if (status === 2) {
            if (appAuthService?.status === 4) {
                return 'Confirming Access';
            }
            if (appAuthService?.graphAuth) {
                if (isAuthlessThanFive) {
                    return 'Confirming Access';
                }
                if (!appAuthService?.company?.approved) {
                    return 'Approval Required';
                } else {
                    return 'Access Required';
                }
            } else {
                return 'Access Required';
            }
        }

        if (status === 4) {
            return 'Initializing';
        }

        if (status === 10) {
            if (statusDisplayDetail?.includes('Missing Regions')) {
                return 'Region not set';
            }
            return 'Provisioning DNS';
        }

        if (status === 11 || status === 12 || status === 13) {
            if (statusDisplayDetail?.includes('Missing Voice Policies')) {
                return 'No Voice Policies';
            } else if (statusDisplayDetail?.includes('Missing Dial Plans')) {
                return 'No Dial Plans';
            } else if (status === 11) {
                return 'Provisioning SBC';
            } else if (status === 12) {
                return 'Activating Teams Phone';
            }
            return 'Provisioning Teams Phone';
        }

        if (status === 14) {
            return 'Pending Activation';
        }
    }

    // Safe fallback with null check
    return statusOptions?.statusLabels?.[0] ?? null;
}

function getStatus(
    statusOptions,
    status,
    statusDisplay,
    statusLabel,
    serviceType,
    appAuthService,
    service,
) {
    // Early return for RingCentral / DialPad (service or auth serviceType) when
    // status is absent and statusDisplay carries the info (statusOptions would
    // be undefined in this case).
    {
        const pendingAccessPlatform = getPendingAccessPlatform(serviceType);
        if (
            pendingAccessPlatform &&
            statusDisplay?.statusDisplayDetail === 'PendingAccess'
        ) {
            return {
                statusLabel: 'Pending Access',
                ...initServiceData.services[pendingAccessPlatform.serviceType][
                    'statusId'
                ][1]['Pending Access'],
            };
        }
    }

    // Old 8x8 services lack a numeric status; derive the card status from statusDisplay.
    if (serviceType === EIGHTBYEIGHT && !statusOptions) {
        const entry = getEightByEightStatusFromDisplay(
            statusDisplay?.statusDisplayDetail,
        );
        if (entry) return entry;
    }

    if (!statusOptions) {
        return null;
    }

    if (
        serviceType === APP_AUTH &&
        appAuthService?.serviceAccountMessage &&
        !appAuthService?.linkedServicesDisabled
    ) {
        return {
            statusLabel: 'Service Account Error',
            ...initServiceData.services[APP_AUTH]['statusId']['-1'][
                'Service Account Error'
            ],
        };
    }

    if (
        serviceType === APP_AUTH &&
        status === 20 &&
        statusLabel === 'Degraded'
    ) {
        return {
            statusLabel: 'Degraded',
            ...statusOptions['Degraded'],
            ...statusDisplayExtendMapping[statusDisplay?.statusDisplayExtended],
        };
    }

    if (
        serviceType === APP_AUTH &&
        !appAuthService?.userSync &&
        appAuthService?.tenantID
    ) {
        if (
            service[OPERATOR_CONNECT] &&
            Object.keys(service[DIRECT_ROUTING]).length === 0
        ) {
            return {
                statusLabel: 'Healthy',
                ...initServiceData.services[APP_AUTH]['statusId'][0]['Healthy'],
            };
        }
    }

    if (
        serviceType === OPERATOR_CONNECT &&
        status === 0 &&
        statusLabel === 'Healthy'
    ) {
        return {
            statusLabel: 'Healthy',
            ...statusOptions['Healthy'],
            ...menuAppAuthMapping[appAuthService ? 'HasAppAuth' : 'NoAppAuth'],
        };
    }

    // Override healthy status if have status display
    if (isSimpleRedirectAuthServiceType(serviceType)) {
        if (statusDisplay?.statusDisplayDetail === 'PairPending') {
            return {
                statusLabel,
                ...initServiceData.services[serviceType]['statusId'][10][
                    'Pair Pending'
                ],
            };
        }
        const pendingAccessPlatform = getPendingAccessPlatform(serviceType);
        if (
            pendingAccessPlatform &&
            (statusDisplay?.statusDisplayDetail === 'PendingAccess' ||
                status === 1 ||
                status === 21)
        ) {
            return {
                statusLabel: 'Pending Access',
                ...initServiceData.services[pendingAccessPlatform.serviceType][
                    'statusId'
                ][1]['Pending Access'],
            };
        }
        if (statusDisplay?.statusDisplayDetail === 'Degraded') {
            const degradedLabel =
                serviceType === EIGHTBYEIGHT_AUTH
                    ? 'Incomplete Config'
                    : 'Degraded';
            return {
                statusLabel: degradedLabel,
                ...initServiceData.services[serviceType]['statusId'][21][
                    degradedLabel
                ],
            };
        }
    }

    return {
        statusLabel,
        ...statusOptions[statusLabel],
    };
}

export const statusReducer = (state, action) => {
    const { serviceType } = action.payload;
    switch (action.type) {
        case `${serviceType}/${loaded_status}`: {
            const { service } = action.payload;
            if (!service) return state;
            const { status, statusDisplay } = service;
            const serviceStatuses = initServiceData.services[serviceType];
            if (!serviceStatuses) return state;
            const isServiceAccountError =
                state.service[APP_AUTH]?.serviceAccountMessage;

            const statusOptions = serviceStatuses['statusId'][status];

            const appAuthService =
                serviceType === APP_AUTH ? service : state.service[APP_AUTH];

            const statusLabel = getStatusLabel(
                statusOptions,
                status,
                statusDisplay,
                serviceType,
                isServiceAccountError,
                appAuthService,
            );
            const cardStatus = getStatus(
                statusOptions,
                status,
                statusDisplay,
                statusLabel,
                serviceType,
                appAuthService,
                state?.service,
            );

            return {
                ...state,
                [serviceType]: {
                    ...state[serviceType],
                    // Stamp the owning service id so consumers can detect a
                    // stale status left over from a previously-viewed company.
                    serviceId: service?.id ?? service?.serviceId,
                    status,
                    statusDisplay,
                    ...cardStatus,
                },
                service: {
                    ...state.service,
                    [serviceType]: service,
                },
            };
        }

        case changed_status: {
            return {};
        }
        default:
            throw new Error('Unknown action: ', +action.type);
    }
};
